<template>
  <div class="page-view chat-view">
    <div class="page-toolbar">
      <el-select
        v-model="selectedWcId"
        class="account-select"
        placeholder="选择微信账号"
        size="small"
        filterable
        style="width: 240px;"
        @change="onAccountChange"
      >
        <el-option
          v-for="acc in accounts"
          :key="acc.wcId"
          :label="(acc.nickName || acc.username || acc.wcId) + (acc.online ? '' : '（离线）')"
          :value="acc.wcId"
        />
      </el-select>
      <el-input
        v-model="contactQuery"
        class="page-search"
        placeholder="搜索联系人 / 群"
        clearable
        size="small"
      />
      <el-button size="small" :disabled="!selectedWcId" :loading="contactsLoading" @click="loadContacts(true)">刷新</el-button>
    </div>

    <div class="chat-body">
      <!-- 左：联系人 / 群 -->
      <div class="contact-pane">
        <div v-if="contactsLoading" class="pane-tip">加载中…</div>
        <div v-else-if="filteredContacts.length === 0" class="pane-tip">暂无聊天记录</div>
        <el-auto-resizer v-else>
          <template #default="{ height, width }">
            <el-table-v2
              :columns="[{ key: 'contact', width }]"
              :data="filteredContacts"
              :width="width"
              :height="height"
              :header-height="0"
              :row-height="72"
              row-key="peerId"
            >
              <template #cell="{ rowData: c }">
                <div
                  class="contact-item"
                  :class="{ active: c.peerId === selectedPeer }"
                  @click="selectContact(c)"
                >
                  <el-avatar shape="square" :size="40" :src="c.avatar">{{ initials(c.name) }}</el-avatar>
                  <div class="contact-meta">
                    <div class="contact-line">
                      <span class="contact-name">
                        <el-tag v-if="c.isGroup" size="small" type="info" effect="plain">群</el-tag>
                        {{ c.name }}
                      </span>
                      <span class="contact-time">{{ shortTime(c.lastTimestamp) }}</span>
                    </div>
                    <div class="contact-preview">{{ preview(c.lastMessageType, c.lastContent) }}</div>
                  </div>
                </div>
              </template>
            </el-table-v2>
          </template>
        </el-auto-resizer>
      </div>

      <!-- 右：消息 -->
      <div class="message-pane">
        <template v-if="selectedPeer">
          <div class="message-header">
            <span class="message-title">{{ currentContactName }}</span>
            <el-pagination
              v-model:current-page="page"
              :page-size="pageSize"
              :total="total"
              layout="prev, pager, next"
              :small="true"
              @current-change="loadHistory"
            />
          </div>
          <el-scrollbar ref="msgScroll" class="message-list">
            <div v-if="historyLoading" class="pane-tip">加载中…</div>
            <div v-else-if="orderedMessages.length === 0" class="pane-tip">无消息</div>
            <div
              v-for="m in orderedMessages"
              :key="m._id"
              class="msg-row"
              :class="{ self: m.data.self }"
            >
              <div class="msg-bubble">
                <div v-if="!m.data.self && selectedPeer.endsWith('@chatroom')" class="msg-sender">
                  {{ m.data.nickName || m.data.fromUser }}
                </div>

                <!-- 视频 / 视频号 -->
                <template v-if="isVideo(m.messageType)">
                  <div class="msg-type-tag">{{ typeLabel(m.messageType) }}</div>
                  <div v-if="videoUrl(m)" class="msg-video">
                    <video :src="videoUrl(m)" controls preload="metadata"></video>
                    <a :href="videoUrl(m)" target="_blank" download class="msg-download-link">下载到本地</a>
                  </div>
                  <!-- 视频号：wkteam 无下载能力，给出明确提示而非可点的失败按钮 -->
                  <div v-else-if="isChannelVideo(m.messageType)">
                    <div class="msg-text">{{ channelTitle(m) }}</div>
                    <div class="msg-note">视频号视频暂不支持下载，请在手机微信中查看</div>
                  </div>
                  <div v-else>
                    <div class="msg-text">{{ channelTitle(m) }}</div>
                    <el-button
                      size="small"
                      type="primary"
                      :loading="vstate(m).loading"
                      @click="downloadVideo(m)"
                    >下载视频</el-button>
                    <div v-if="vstate(m).error" class="msg-error">{{ vstate(m).error }}</div>
                  </div>
                </template>

                <!-- 语音 -->
                <template v-else-if="isVoice(m.messageType)">
                  <div class="msg-type-tag">语音</div>
                  <audio v-if="m.data.file" :src="m.data.file" controls></audio>
                  <span v-else class="msg-text">[语音]</span>
                </template>

                <!-- 图片 -->
                <template v-else-if="isImage(m.messageType)">
                  <div class="msg-type-tag">图片</div>
                  <span class="msg-text">[图片]</span>
                </template>

                <!-- 其它 -->
                <template v-else>
                  <div v-if="typeLabel(m.messageType)" class="msg-type-tag">{{ typeLabel(m.messageType) }}</div>
                  <div class="msg-text">{{ preview(m.messageType, m.data.content) }}</div>
                </template>

                <div class="msg-time">{{ fullTime(m.data.timestamp) }}</div>
              </div>
            </div>
          </el-scrollbar>
        </template>
        <div v-else class="pane-tip pane-tip-center">从左侧选择一个联系人或群查看聊天记录</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { ElMessage } from "element-plus";
import {
  getWechatAccounts,
  getMessageContacts,
  getMessageHistory,
  downloadMessageVideo,
} from "../api/http";
import type { ChatMessage, MessageContact, WechatAccount } from "../api/models";

// Message type labels (subset the UI cares about; unknown → "").
const TYPE_LABEL: Record<string, string> = {
  "60002": "图片", "80002": "图片",
  "60003": "视频", "80003": "视频",
  "60004": "语音", "80004": "语音",
  "60007": "链接", "80007": "链接",
  "60008": "文件", "80008": "文件",
  "60010": "小程序", "80010": "小程序",
  "60014": "引用", "80014": "引用",
  "60017": "视频号", "80017": "视频号",
  "60020": "位置", "80020": "位置",
  "60021": "音乐", "80021": "音乐",
};
const VIDEO_TYPES = new Set(["60003", "80003", "60017", "80017"]);
const VOICE_TYPES = new Set(["60004", "80004"]);
const IMAGE_TYPES = new Set(["60002", "80002"]);

const isVideo = (t: string) => VIDEO_TYPES.has(t);
// 视频号 (channels / finder feed). wkteam has no download API for these, so the
// UI shows a note rather than a download button (which would always fail).
const isChannelVideo = (t: string) => t === "60017" || t === "80017";
const isVoice = (t: string) => VOICE_TYPES.has(t);
const isImage = (t: string) => IMAGE_TYPES.has(t);
const typeLabel = (t: string) => TYPE_LABEL[t] || "";

const accounts = ref<WechatAccount[]>([]);
const selectedWcId = ref("");

const contacts = ref<MessageContact[]>([]);
const contactsLoading = ref(false);
const contactQuery = ref("");
const selectedPeer = ref("");

const messages = ref<ChatMessage[]>([]);
const historyLoading = ref(false);
const page = ref(1);
const pageSize = ref(30);
const total = ref(0);

// Per-message video download state, keyed by message _id.
const videoStates = reactive<Record<string, { loading: boolean; url: string; error: string }>>({});
const vstate = (m: ChatMessage) => {
  if (!videoStates[m._id]) videoStates[m._id] = { loading: false, url: "", error: "" };
  return videoStates[m._id];
};

const filteredContacts = computed(() => {
  const q = contactQuery.value.trim().toLowerCase();
  if (!q) return contacts.value;
  return contacts.value.filter(
    (c) => c.name.toLowerCase().includes(q) || c.peerId.toLowerCase().includes(q)
  );
});

const currentContactName = computed(
  () => contacts.value.find((c) => c.peerId === selectedPeer.value)?.name || selectedPeer.value
);

// Display oldest→newest within the page (server returns newest-first).
const orderedMessages = computed(() => [...messages.value].reverse());

const initials = (name: string) => (name ? name.slice(0, 1).toUpperCase() : "?");

const shortTime = (ts?: number) => {
  if (!ts) return "";
  const d = new Date(ts * 1000);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("zh-CN", { month: "2-digit", day: "2-digit" });
};

const fullTime = (ts?: number) =>
  ts ? new Date(ts * 1000).toLocaleString("zh-CN") : "";

// A one-line preview for a contact's last message / non-media bubble.
const preview = (type: string, content: string) => {
  if (isVideo(type)) return type === "60017" || type === "80017" ? "[视频号]" : "[视频]";
  if (isVoice(type)) return "[语音]";
  if (isImage(type)) return "[图片]";
  const label = typeLabel(type);
  const text = (content || "").replace(/\s+/g, " ").trim();
  if (text && !text.startsWith("<")) return text.slice(0, 60);
  return label ? `[${label}]` : text.slice(0, 60);
};

// For 视频号 messages, pull the <title>/<desc> out of the appmsg XML for a hint.
const channelTitle = (m: ChatMessage) => {
  const xml = m.data.content || "";
  const desc = xml.match(/<desc>([\s\S]*?)<\/desc>/);
  if (desc && desc[1].trim()) return desc[1].trim().slice(0, 80);
  const title = xml.match(/<title>([\s\S]*?)<\/title>/);
  if (title && title[1].trim()) return title[1].trim().slice(0, 80);
  return isVideo(m.messageType) ? "[视频]" : "";
};

// Resolved/cached video url for a message: either a freshly downloaded one or
// the cached data.file mirrored by the server.
const videoUrl = (m: ChatMessage) => {
  const st = videoStates[m._id];
  if (st && st.url) return st.url;
  if (m.data.file) return m.data.file;
  return "";
};

const loadAccounts = () => {
  getWechatAccounts().then((res) => {
    if (res.successful) {
      accounts.value = res.result;
      const initial = res.result.find((a) => a.online) ?? res.result[0];
      if (initial && !selectedWcId.value) {
        selectedWcId.value = initial.wcId;
        loadContacts();
      }
    } else {
      ElMessage.error(res.message);
    }
  });
};

const onAccountChange = () => {
  ++historyRequest;
  historyLoading.value = false;
  total.value = 0;
  contacts.value = [];
  selectedPeer.value = "";
  messages.value = [];
  loadContacts();
};

let contactsRequest = 0;
let historyRequest = 0;
const loadContacts = (refresh = false) => {
  if (!selectedWcId.value) return;
  const request = ++contactsRequest;
  contactsLoading.value = true;
  getMessageContacts(selectedWcId.value, refresh)
    .then((res) => {
      if (request !== contactsRequest) return;
      if (res.successful) contacts.value = res.result;
      else ElMessage.error(res.message);
    })
    .catch(() => { if (request === contactsRequest) ElMessage.error("加载联系人失败，请重试"); })
    .finally(() => { if (request === contactsRequest) contactsLoading.value = false; });
};

const selectContact = (c: MessageContact) => {
  selectedPeer.value = c.peerId;
  page.value = 1;
  loadHistory();
};

const loadHistory = () => {
  if (!selectedWcId.value || !selectedPeer.value) return;
  const request = ++historyRequest;
  historyLoading.value = true;
  getMessageHistory(selectedWcId.value, selectedPeer.value, page.value, pageSize.value)
    .then((res) => {
      if (request !== historyRequest) return;
      if (res.successful) {
        messages.value = res.result.items;
        total.value = res.result.total;
      } else {
        ElMessage.error(res.message);
      }
    })
    .catch(() => { if (request === historyRequest) ElMessage.error("加载聊天记录失败，请重试"); })
    .finally(() => { if (request === historyRequest) historyLoading.value = false; });
};

const downloadVideo = (m: ChatMessage) => {
  const st = vstate(m);
  st.loading = true;
  st.error = "";
  downloadMessageVideo(selectedWcId.value, {
    newMsgId: m.data.newMsgId,
    msgId: m.data.msgId,
  })
    .then((res) => {
      if (res.successful) {
        st.url = res.result.url;
        ElMessage.success("视频已就绪");
      } else {
        st.error = res.message;
        ElMessage.error(res.message);
      }
    })
    .catch((e) => {
      st.error = (e && e.message) || "下载失败";
      ElMessage.error(st.error);
    })
    .finally(() => (st.loading = false));
};

loadAccounts();
</script>

<style scoped>
.chat-view {
  height: 100%;
}
.chat-body {
  flex: 1;
  display: flex;
  min-height: 0;
  border: 1px solid var(--el-border-color-light);
  border-radius: 6px;
  overflow: hidden;
}
.contact-pane {
  width: 280px;
  border-right: 1px solid var(--el-border-color-light);
  flex: none;
  position: relative;
}
.contact-item {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid var(--el-border-color-lighter);
}
.contact-item:hover { background: var(--el-fill-color-light); }
.contact-item.active { background: var(--el-color-primary-light-9); }
.contact-meta { flex: 1; min-width: 0; }
.contact-line { display: flex; justify-content: space-between; gap: 6px; }
.contact-name {
  font-weight: 600; font-size: 14px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.contact-time { color: var(--el-text-color-secondary); font-size: 12px; flex: none; }
.contact-preview {
  color: var(--el-text-color-secondary); font-size: 12px; margin-top: 2px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.message-pane { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.message-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px; border-bottom: 1px solid var(--el-border-color-light);
}
.message-title { font-weight: 600; }
.message-list { flex: 1; padding: 12px; background: var(--el-fill-color-lighter); }
.msg-row { display: flex; margin-bottom: 12px; }
.msg-row.self { justify-content: flex-end; }
.msg-bubble {
  max-width: 70%;
  background: #fff;
  border-radius: 8px;
  padding: 8px 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}
.msg-row.self .msg-bubble { background: var(--el-color-primary-light-8); }
.msg-sender { font-size: 12px; color: var(--el-color-primary); margin-bottom: 4px; }
.msg-type-tag { font-size: 11px; color: var(--el-text-color-secondary); margin-bottom: 4px; }
.msg-text { white-space: pre-wrap; word-break: break-word; font-size: 14px; }
.msg-time { font-size: 11px; color: var(--el-text-color-secondary); margin-top: 4px; text-align: right; }
.msg-video video { max-width: 280px; max-height: 360px; border-radius: 6px; display: block; }
.msg-download-link { font-size: 12px; display: inline-block; margin-top: 4px; }
.msg-error { color: var(--el-color-danger); font-size: 12px; margin-top: 4px; }
.msg-note { color: var(--el-text-color-secondary); font-size: 12px; margin-top: 4px; }
.pane-tip { color: var(--el-text-color-secondary); padding: 16px; text-align: center; }
.pane-tip-center { margin: auto; }
</style>
